import { CdkDragExit, DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';

import {
  AfterViewInit,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatIconModule } from '@angular/material/icon';
import { ResourceManager, ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import {
  _AbcButtonComponent,
  _AbcContextMenuComponent,
  _AbcScrollViewComponent,
  _AbcSortableComponent,
} from '@barthauer/abc/ui-components';
import {
  AbcKanbanContextMenuItem,
  AbcKanbanGroup,
  AbcKanbanMenuItem,
  AbcKanbanRessource,
  AbcKanbanTask,
} from './abc-kanban';

@Component({
  selector: 'abc-kanban',
  standalone: true,
  imports: [
    CommonModule,
    _AbcButtonComponent,
    _AbcContextMenuComponent,
    _AbcScrollViewComponent,
    _AbcSortableComponent,
    FlexLayoutModule,
    MatIconModule,
    DragDropModule,
    MatTooltipModule,
  ],
  templateUrl: './abc-kanban.component.html',
  styleUrls: ['./abc-kanban.component.scss'],
})
export class AbcKanbanComponent implements AfterViewInit {
  @ContentChild(TemplateRef) cardContent: TemplateRef<any>;
  @Output() onTaskDropped: EventEmitter<AbcKanbanTask> = new EventEmitter();
  @Output() onResourceDropped: EventEmitter<AbcKanbanTask> = new EventEmitter<AbcKanbanTask>();

  public dataByGroup: Map<string, AbcKanbanTask[]> = new Map<string, AbcKanbanTask[]>();
  private _filteredDataByGroup: Map<string, AbcKanbanTask[]> = new Map<string, AbcKanbanTask[]>();
  public get filteredDataByGroup(): Map<string, AbcKanbanTask[]> {
    if (!this._filteredDataByGroup) {
      this._filteredDataByGroup = new Map<string, AbcKanbanTask[]>();
    }
    if (this._kanbanGroups && this._kanbanGroups.length > 0) {
      this._kanbanGroups.forEach((kanbanGroup: AbcKanbanGroup) => {
        if (!this._filteredDataByGroup.get(kanbanGroup.value)) {
          const tasksArray: AbcKanbanTask[] = [];
          this._filteredDataByGroup.set(kanbanGroup.value, tasksArray);
        }
      });
    }
    return this._filteredDataByGroup;
  }
  public transferringItem: any | undefined = undefined;
  public resources = ResourceManager.getResources('abc-kanban');
  public resKanban = ResourceSet.Empty;

  private _kanbanMenuItems: AbcKanbanMenuItem[] = [];
  private _kanbanGroups: AbcKanbanGroup[] = [];
  private _kanbanContextMenuItems: AbcKanbanContextMenuItem[] = [];
  private _kanbanRessources: AbcKanbanRessource[] = [];
  private _data: any;
  private _infoText: string;

  @ViewChild('dropZone', { static: false }) dropZone: ElementRef;

  @Input()
  get kanbanMenuItems(): AbcKanbanMenuItem[] {
    return this._kanbanMenuItems;
  }
  set kanbanMenuItems(value: AbcKanbanMenuItem[]) {
    this._kanbanMenuItems = value;
  }

  @Input()
  get kanbanGroups(): AbcKanbanGroup[] {
    return this._kanbanGroups;
  }
  set kanbanGroups(value: AbcKanbanGroup[]) {
    this._kanbanGroups = value;
  }

  @Input()
  get kanbanContextMenuItems(): AbcKanbanContextMenuItem[] {
    return this._kanbanContextMenuItems;
  }
  set kanbanContextMenuItems(value: AbcKanbanContextMenuItem[]) {
    this._kanbanContextMenuItems = value;
  }

  @Input()
  get kanbanRessources(): AbcKanbanRessource[] {
    return this._kanbanRessources;
  }
  set kanbanRessources(value: AbcKanbanRessource[]) {
    this._kanbanRessources = value;
  }

  @Input()
  get data(): any[] {
    return this._data;
  }
  set data(value: any[]) {
    this._data = value;

    this.dataByGroup.clear();
    this._filteredDataByGroup.clear();

    if (this._data instanceof Map) {
      this.dataByGroup = this._data;
    } else if (this._data instanceof Array && this._data[0] instanceof AbcKanbanTask) {
      this._data.forEach((kanbanTask: AbcKanbanTask) => {
        this.addToGroup(kanbanTask);
      });
    } else if (this._data instanceof Object) {
      //Make KanbanDataThingyClass[] instead of Object
      //BuildDataByGroup
    } else {
      //error
    }

    this.setDataSource();
  }

  @Input()
  get infoText(): string {
    return this._infoText;
  }
  set infoText(value: string) {
    this._infoText = value;
  }

  constructor(private resourceService: ResourceManagerService) {}

  async ngAfterViewInit(): Promise<void> {
    this.resKanban = await this.resourceService.getResourceSet('Kaos-APM-Kanban');
  }

  private addToGroup(kanbanTask: AbcKanbanTask) {
    let kanbanTasks = this.dataByGroup.get(kanbanTask.group);

    if (!kanbanTasks) {
      kanbanTasks = [];
      this.dataByGroup.set(kanbanTask.group, kanbanTasks);
    }

    kanbanTasks.push(kanbanTask);
  }

  private setDataSource() {
    const selectedRessources: AbcKanbanRessource[] = this.getSelectedRessources();

    this._filteredDataByGroup.clear();

    if (selectedRessources.length > 0) {
      this.dataByGroup.forEach((kanbanTasks: AbcKanbanTask[], groupValue: string) => {
        this._filteredDataByGroup.set(groupValue, new Array<AbcKanbanTask>());

        kanbanTasks.forEach((kanbanTask: AbcKanbanTask) => {
          selectedRessources.forEach((selectedRessource: AbcKanbanRessource) => {
            if (selectedRessource.id === kanbanTask.idRefRessource) {
              this._filteredDataByGroup.get(groupValue)?.push(kanbanTask);
            }
          });
        });
      });
    } else {
      this.dataByGroup.forEach((kanbanTasks: AbcKanbanTask[], groupValue: string) => {
        this._filteredDataByGroup.set(groupValue, kanbanTasks);
      });
    }
  }

  public onRessourceClick(kanbanRessource: AbcKanbanRessource) {
    kanbanRessource.selected = !kanbanRessource.selected;

    this.setDataSource();
  }

  private getSelectedRessources(): AbcKanbanRessource[] {
    const selectedRessources: AbcKanbanRessource[] = [];

    this.kanbanRessources.forEach((kanbanResource: AbcKanbanRessource) => {
      if (kanbanResource.selected) {
        selectedRessources.push(kanbanResource);
      }
    });

    return selectedRessources;
  }

  public onMenuItemClick(kanbanMenuItem: AbcKanbanMenuItem) {
    kanbanMenuItem.onItemClick();
  }
  public onContextMenuItemClick(e: any, kanbanTask: AbcKanbanTask) {
    e.itemData.onItemClick(kanbanTask);
  }

  onGroupDragStart(e: any) {
    e.cancel = true;
  }

  onTaskDragStart(e: any) {
    e.itemData = e.fromData[e.fromIndex];
  }

  onTaskDrop(e: any, kanbanGroup: AbcKanbanGroup) {
    e.fromData.splice(e.fromIndex, 1);
    e.toData.splice(e.toIndex, 0, e.itemData);
    if (e.itemData.group !== kanbanGroup.value) {
      e.itemData.group = kanbanGroup.value;
      this.onTaskDropped.emit(e.itemData);
    }
  }

  public ressourceDropped(event: any) {
    if (event.item.data instanceof AbcKanbanRessource && event.container.id === 'kanbanCard') {
      const task: any = event.container.data[event.currentIndex - 1];
      const resource = event.item.data;
      if (task instanceof AbcKanbanTask && resource instanceof AbcKanbanRessource && resource.id) {
        task.idRefRessource = resource.id;
        task.data.IdRefPerson = task.idRefRessource;
        task.data.PersonAbbreviation = resource.abbreviation ?? null;
        task.data.Image = resource.image;
        if (task.data.PersonAbbreviation === '?') {
          task.data.PersonAbbreviation = '';
        }
        this.onResourceDropped.emit(task);
      }
    }

    this.transferringItem = undefined;
  }

  public resourceEntered(event: any) {
    this.transferringItem = undefined;
  }

  public resourceExited(e: CdkDragExit<AbcKanbanRessource[]>) {
    this.transferringItem = e.item.data;
  }
}
